import React, { useEffect, useState } from "react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Loader2, ScanLine, Users, Plus, LogOut, CheckCircle2, XCircle, Wallet, CalendarDays, Camera, CameraOff } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { formatSeat } from "../components/SeatMap";
import { Html5Qrcode } from "html5-qrcode";

const extractCode = (text) => {
  if (!text) return "";
  const m = String(text).match(/\/(?:voucher|ticket|pass)\/([^/?#\s]+)/i);
  return (m ? m[1] : String(text).trim()).toUpperCase();
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function StaffDoor() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState({});
  const [code, setCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guests, setGuests] = useState([]);
  const [sale, setSale] = useState({ tier_id: "", name: "", quantity: 1, pay_method: "cash", comp: false, comp_reason: "" });
  const [takings, setTakings] = useState(null);
  const [scanning, setScanning] = useState(false);
  const qrRef = React.useRef(null);
  const [flash, setFlash] = useState(null); // 'ok' | 'bad'

  const loadTakings = () => api.get("/staff/me/takings").then(({ data }) => setTakings(data)).catch(() => {});

  const feedback = (kind) => {
    setFlash(kind); setTimeout(() => setFlash(null), 900);
    try { if (navigator.vibrate) navigator.vibrate(kind === "ok" ? 120 : [90, 60, 90, 60, 90]); } catch { /* noop */ }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ac = new AC();
      const beep = (freq, start, dur) => {
        const o = ac.createOscillator(), g = ac.createGain();
        o.connect(g); g.connect(ac.destination);
        o.frequency.value = freq; o.type = "sine";
        g.gain.setValueAtTime(0.001, ac.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.35, ac.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + start + dur);
        o.start(ac.currentTime + start); o.stop(ac.currentTime + start + dur);
      };
      if (kind === "ok") beep(880, 0, 0.18);
      else { beep(300, 0, 0.22); beep(300, 0.28, 0.22); }
    } catch { /* noop */ }
  };

  const checkinCode = async (raw) => {
    const c = extractCode(raw);
    if (!c) return;
    setBusy(true); setScanResult(null);
    try {
      const { data } = await api.post("/staff/checkin", { code: c });
      setScanResult(data); setCode(""); loadTakings();
      feedback(data.status === "ok" || data.status === "pass" ? "ok" : "bad");
    } catch (e) {
      setScanResult({ status: "error", message: e?.response?.data?.detail || "Ticket not found" });
      feedback("bad");
    } finally { setBusy(false); }
  };

  const stopScanner = async () => {
    try { if (qrRef.current) { await qrRef.current.stop(); await qrRef.current.clear(); } } catch { /* noop */ }
    qrRef.current = null; setScanning(false);
  };
  const startScanner = async () => {
    setScanResult(null); setScanning(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const q = new Html5Qrcode("qr-reader");
      qrRef.current = q;
      await q.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        async (decoded) => { await stopScanner(); await checkinCode(decoded); },
        () => {});
    } catch (e) {
      setScanning(false);
      toast({ title: "Camera unavailable", description: "Allow camera access or type the code instead.", variant: "destructive" });
    }
  };
  useEffect(() => () => { stopScanner(); }, []); // eslint-disable-line

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "staff") { nav("/"); return; }
    api.get("/staff/context").then(({ data }) => setCtx(data)).catch(() => setCtx({ events: [] }));
    loadTakings();
  }, [user, loading]); // eslint-disable-line

  const openEvent = (ev, m) => {
    stopScanner();
    setOpen(ev.id); setMode({ ...mode, [ev.id]: m }); setScanResult(null); setCode("");
    const firstTier = (ev.ticket_tiers || [])[0];
    setSale({ tier_id: firstTier?.id || "", name: "", quantity: 1, pay_method: "cash", comp: false, comp_reason: "" });
    if (m === "guests") api.get(`/staff/events/${ev.id}/guests`).then(({ data }) => setGuests(data.guests || [])).catch(() => setGuests([]));
  };

  const doCheckin = async () => {
    if (!code.trim()) return;
    await checkinCode(code);
  };

  const doSell = async (ev) => {
    if (!sale.tier_id) { toast({ title: "Pick a ticket type", variant: "destructive" }); return; }
    if (sale.comp && !sale.comp_reason.trim()) { toast({ title: "A comp reason is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const payload = {
        product_id: ev.id, tier_id: sale.tier_id, quantity: Math.max(1, parseInt(sale.quantity) || 1),
        paid: !sale.comp, pay_method: sale.pay_method,
        entries: sale.name.trim() ? [{ name: sale.name.trim(), email: "" }] : [],
        comp_reason: sale.comp ? sale.comp_reason.trim() : "",
      };
      const { data } = await api.post("/staff/box-office", payload);
      toast({ title: sale.comp ? "Comp issued" : "Sale recorded", description: `${data.issued || 1} ticket(s) for ${ev.title}.` });
      setSale({ ...sale, name: "", quantity: 1, comp_reason: "" });
      loadTakings();
    } catch (e) { toast({ title: "Couldn't complete", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  if (loading || !ctx) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>;
  const biz = ctx.business || {};
  const brand = biz.brand_color || "#16243b";

  return (
    <div className="min-h-screen bg-[#f6f8fb]" data-testid="staff-door">
      {flash && (
        <div data-testid="scan-flash" className={`fixed inset-0 z-[100] pointer-events-none flex items-center justify-center ${flash === "ok" ? "bg-green-500/90" : "bg-red-600/90"}`} style={{ animation: "fadeOut .9s ease forwards" }}>
          {flash === "ok" ? <CheckCircle2 className="text-white" size={120} /> : <XCircle className="text-white" size={120} />}
          <style>{`@keyframes fadeOut{0%{opacity:1}70%{opacity:1}100%{opacity:0}}`}</style>
        </div>
      )}
      <header className="text-white px-5 py-4 flex items-center justify-between" style={{ background: brand }}>
        <div className="flex items-center gap-3">
          {biz.logo_url ? <img src={biz.logo_url} alt="" className="h-8" /> : null}
          <div>
            <p className="font-semibold leading-tight">{biz.name} · Door</p>
            <p className="text-xs opacity-80">Hi {ctx.staff?.name}</p>
          </div>
        </div>
        <button onClick={() => { logout(); nav("/login"); }} data-testid="staff-logout" className="flex items-center gap-1.5 text-sm bg-white/15 hover:bg-white/25 rounded-full px-4 py-2 transition-colors"><LogOut size={15} /> Sign out</button>
      </header>

      <div className="max-w-3xl mx-auto p-5 space-y-5">
        {takings && (
          <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] flex items-center gap-4" data-testid="staff-takings">
            <div className="w-11 h-11 rounded-xl bg-[#eefafa] flex items-center justify-center"><Wallet className="text-[#2ec9c9]" size={22} /></div>
            <div className="flex-1">
              <p className="font-semibold text-[#16243b]">Your takings this shift</p>
              <p className="text-sm text-[#5a6b80]">Cash <strong className="text-[#16243b]">£{takings.cash.toFixed(2)}</strong> · Card <strong className="text-[#16243b]">£{takings.card.toFixed(2)}</strong> · {takings.tickets_sold} sold · {takings.comps} comp(s)</p>
            </div>
            <div className="text-right"><p className="text-2xl font-bold" style={{ color: brand }}>£{takings.total.toFixed(2)}</p><p className="text-xs text-[#8a97a8]">total</p></div>
          </div>
        )}

        {ctx.events.length === 0 && <div className="bg-white rounded-2xl p-10 text-center text-[#8a97a8]" data-testid="staff-no-events">No events assigned to you yet. Ask the promoter to assign you.</div>}

        {ctx.events.map((ev) => (
          <div key={ev.id} className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden" data-testid={`staff-event-${ev.id}`}>
            <div className="p-5">
              <p className="font-semibold text-[#16243b] text-lg">{ev.title}</p>
              <p className="text-sm text-[#8a97a8] flex items-center gap-1.5 mt-0.5"><CalendarDays size={14} /> {fmtDate(ev.event_date)} · {ev.venue_name}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEvent(ev, "checkin")} data-testid={`staff-scan-${ev.id}`} className={`flex-1 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border transition-colors ${open === ev.id && mode[ev.id] === "checkin" ? "bg-[#2ec9c9] text-white border-[#2ec9c9]" : "border-gray-200 text-[#16243b] hover:bg-gray-50"}`}><ScanLine size={15} /> Scan</button>
                <button onClick={() => openEvent(ev, "sell")} data-testid={`staff-sell-${ev.id}`} className={`flex-1 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border transition-colors ${open === ev.id && mode[ev.id] === "sell" ? "bg-[#2ec9c9] text-white border-[#2ec9c9]" : "border-gray-200 text-[#16243b] hover:bg-gray-50"}`}><Plus size={15} /> Sell / comp</button>
                <button onClick={() => openEvent(ev, "guests")} data-testid={`staff-guests-${ev.id}`} className={`flex-1 rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border transition-colors ${open === ev.id && mode[ev.id] === "guests" ? "bg-[#2ec9c9] text-white border-[#2ec9c9]" : "border-gray-200 text-[#16243b] hover:bg-gray-50"}`}><Users size={15} /> Guests</button>
              </div>
            </div>

            {open === ev.id && (
              <div className="border-t border-gray-100 p-5 bg-[#f9fbfd]">
                {mode[ev.id] === "checkin" && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && doCheckin()} data-testid="staff-code-input" placeholder="Enter or scan ticket code" className="flex-1 rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9] font-mono" />
                      <button onClick={doCheckin} disabled={busy} data-testid="staff-checkin-btn" className="gp-btn-teal rounded-xl px-6 font-medium disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={18} /> : "Check in"}</button>
                    </div>
                    {scanning ? (
                      <div className="space-y-2">
                        <div id="qr-reader" data-testid="qr-reader" className="rounded-xl overflow-hidden bg-black mx-auto" style={{ maxWidth: 320 }} />
                        <button onClick={stopScanner} data-testid="qr-stop" className="w-full rounded-xl py-2.5 text-sm font-medium border border-gray-200 text-[#5a6b80] hover:bg-gray-50 flex items-center justify-center gap-1.5"><CameraOff size={15} /> Stop camera</button>
                      </div>
                    ) : (
                      <button onClick={startScanner} data-testid="qr-start" className="w-full rounded-xl py-2.5 text-sm font-medium border border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa] flex items-center justify-center gap-1.5"><Camera size={16} /> Scan with camera</button>
                    )}
                    {scanResult && (
                      scanResult.status === "pass" ? (
                        <div className="rounded-xl p-5 bg-green-600 text-white shadow-lg" data-testid="staff-scan-result">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 size={30} />
                            <div>
                              <p className="font-bold text-lg leading-tight">✓ VALID · {scanResult.pass_type}</p>
                              <p className="text-sm text-white/90">{scanResult.holder_name || "Pass holder"} — access to all live events.</p>
                            </div>
                          </div>
                        </div>
                      ) : scanResult.status === "revoked" ? (
                        <div className="rounded-xl p-5 bg-red-600 text-white shadow-lg animate-pulse" data-testid="staff-scan-result">
                          <div className="flex items-center gap-3">
                            <XCircle size={30} />
                            <div>
                              <p className="font-bold text-lg leading-tight">✕ PASS REVOKED</p>
                              <p className="text-sm text-white/90">{scanResult.pass_type} pass for {scanResult.holder_name || "this holder"} is no longer valid. Do not admit.</p>
                            </div>
                          </div>
                        </div>
                      ) : scanResult.status === "expired" ? (
                        <div className="rounded-xl p-5 bg-red-600 text-white shadow-lg animate-pulse" data-testid="staff-scan-result">
                          <div className="flex items-center gap-3">
                            <XCircle size={30} />
                            <div>
                              <p className="font-bold text-lg leading-tight">✕ PASS EXPIRED</p>
                              <p className="text-sm text-white/90">{scanResult.pass_type} pass for {scanResult.holder_name || "this holder"} expired{scanResult.expires_at ? ` on ${new Date(scanResult.expires_at).toLocaleDateString()}` : ""}. Do not admit.</p>
                            </div>
                          </div>
                        </div>
                      ) : scanResult.status === "ok" ? (
                        <div className="rounded-xl p-4 flex items-center gap-3 bg-green-50 text-green-700" data-testid="staff-scan-result">
                          <CheckCircle2 size={26} />
                          <div><p className="font-semibold">Checked in — {scanResult.attendee_name || scanResult.tier_name}</p>{scanResult.tier_name && <p className="text-sm opacity-80">{scanResult.tier_name}</p>}</div>
                        </div>
                      ) : (
                        <div className="rounded-xl p-5 bg-red-600 text-white shadow-lg animate-pulse" data-testid="staff-scan-result">
                          <div className="flex items-center gap-3">
                            <XCircle size={30} />
                            <div>
                              <p className="font-bold text-lg leading-tight">{scanResult.status === "already" ? "⚠ ALREADY CHECKED IN" : "✕ INVALID TICKET"}</p>
                              <p className="text-sm text-white/90">
                                {scanResult.status === "already"
                                  ? `${scanResult.attendee_name || scanResult.tier_name || "This ticket"} was scanned${scanResult.checked_in_at ? ` at ${fmtDate(scanResult.checked_in_at)}` : ""} — possible duplicate.`
                                  : (scanResult.message || "Do not admit.")}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                {mode[ev.id] === "sell" && (
                  <div className="space-y-3">
                    <select value={sale.tier_id} onChange={(e) => setSale({ ...sale, tier_id: e.target.value })} data-testid="staff-tier-select" className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9]">
                      {(ev.ticket_tiers || []).map((t) => <option key={t.id} value={t.id}>{t.name} — £{Number(t.price).toFixed(2)}</option>)}
                    </select>
                    <div className="flex gap-2">
                      <input value={sale.name} onChange={(e) => setSale({ ...sale, name: e.target.value })} data-testid="staff-buyer-name" placeholder="Attendee name (optional)" className="flex-1 rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9]" />
                      <input type="number" min="1" value={sale.quantity} onChange={(e) => setSale({ ...sale, quantity: e.target.value })} data-testid="staff-qty" className="w-20 rounded-xl border border-gray-200 px-3 py-3 outline-none focus:border-[#2ec9c9]" />
                    </div>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={sale.comp} onChange={(e) => setSale({ ...sale, comp: e.target.checked })} data-testid="staff-comp-toggle" className="w-4 h-4 accent-[#2ec9c9]" />
                      <span className="text-sm text-[#16243b]">Issue as a free comp (no charge)</span>
                    </label>
                    {sale.comp ? (
                      <input value={sale.comp_reason} onChange={(e) => setSale({ ...sale, comp_reason: e.target.value })} data-testid="staff-comp-reason" placeholder="Reason for comp (required)…" className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9]" />
                    ) : (
                      <div className="flex gap-2">
                        {["cash", "card"].map((pm) => (
                          <button key={pm} onClick={() => setSale({ ...sale, pay_method: pm })} data-testid={`staff-pay-${pm}`} className={`flex-1 rounded-xl py-2.5 text-sm font-medium border capitalize transition-colors ${sale.pay_method === pm ? "bg-[#16243b] text-white border-[#16243b]" : "border-gray-200 text-[#5a6b80] hover:bg-gray-50"}`}>{pm}</button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => doSell(ev)} disabled={busy} data-testid="staff-sell-confirm" className="w-full gp-btn-teal rounded-xl py-3 font-medium disabled:opacity-60">{busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : sale.comp ? "Issue comp" : "Record sale"}</button>
                  </div>
                )}

                {mode[ev.id] === "guests" && (
                  <div>
                    <p className="text-sm text-[#5a6b80] mb-2">{guests.length} guest(s) · {guests.filter((g) => g.checked_in).length} checked in</p>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                      {guests.map((g) => (
                        <div key={g.code} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm border border-gray-100" data-testid={`staff-guest-${g.code}`}>
                          <div><span className="font-medium text-[#16243b]">{g.attendee_name || "Guest"}</span><span className="text-[#8a97a8]"> · {g.tier_name}{g.seat ? ` · ${formatSeat(g.seat)}` : ""}</span></div>
                          {g.checked_in ? <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle2 size={13} /> In</span> : <span className="text-xs text-[#8a97a8] font-mono">{g.code}</span>}
                        </div>
                      ))}
                      {guests.length === 0 && <p className="text-center text-[#8a97a8] py-6">No tickets yet.</p>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
